import { isNullOrEmpty } from '@abp/ng.core';
import { HttpClient } from '@angular/common/http';
import { Component, Injector } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppUserService } from '@proxy/users/app-user.service';
import { OnboardingStep } from '@proxy/users/onboarding-step.enum';
import {
  CreatorInfoAddressDto,
  CreatorProfileDto,
  GetInforOnboardingDto,
  RandomWishListItemDto,
} from '@proxy/wish-list/dtos/onboarding-step/models';
import { OnboardingService } from '@proxy/wish-list/onboarding.service';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { catchError, finalize } from 'rxjs';
import { AppComponentBase } from 'src/app/shared/app-component-base';
import { ValidationComponent } from '../../dft/component/validation-messages.component';

interface OnboardingStepInfo {
  title: string;
  subtitle: string;
  component: string;
}

@Component({
  selector: 'app-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrls: ['./onboarding.component.scss'],
})
export class OnboardingComponent extends AppComponentBase {
  currentStep: OnboardingStep = OnboardingStep.Step1_Profile;
  visitedSteps: number[] = [0];
  steps: OnboardingStepInfo[] = [
    {
      title: this.L('::Onboarding:Profile:Title'),
      subtitle: this.L('::Onboarding:Profile:SubTitle'),
      component: 'profile',
    },
    {
      title: this.L('::Onboarding:Address:Title'),
      subtitle: this.L('::Onboarding:Address:SubTitle'),
      component: 'address',
    },
    {
      title: this.L('::Onboarding:Wishlist:Title'),
      subtitle: this.L('::Onboarding:Wishlist:SubTitle'),
      component: 'wishlist',
    },
    {
      title: this.L('::Onboarding:Share:Title'),
      subtitle: this.L('::Onboarding:Share:SubTitle'),
      component: 'share',
    },
  ];

  // Forms
  profileForm: FormGroup;
  mediaForm: FormGroup;
  addressForm: FormGroup;
  avatarFileName: string = '';
  coverFileName: string = '';
  videoFileName: string = '';

  imageCheck = ['jpg', 'png', 'jpeg'];
  videoCheck = ['mp4', 'mov'];
  imageAccepts: string = 'image/jpeg, image/png, image/jpg';
  videoAccepts: string = 'video/mp4, video/quicktime';
  URLUploadAvatar = this.serverUrl + `/api/Upload/UploadProfileImage`;
  URLUploadCover = this.serverUrl + `/api/Upload/UploadCoverImage`;
  URLUploadVideo = this.serverUrl + `/api/Upload/UploadShortVideo?videoDirectionType=`;

  // List of provinces
  prefectureOptions = [];

  // Sample product list for wishlist
  sampleWishlistItems: RandomWishListItemDto[] = [];

  // List of selected products
  selectedWishlistItems: RandomWishListItemDto[] = [];

  // Track items that have been added to the wishlist (store IDs)
  addedWishlistItems: string[] = [];

  // Storing data from API
  private onboardingData: GetInforOnboardingDto | null = null;
  private onStepChangeCallback: (step: OnboardingStep) => void;

  loadingAvatar = false;
  avatarPreview: string | ArrayBuffer | null = null;
  loadingCover = false;
  coverPreview: string | ArrayBuffer | null = null;
  loadingVideo = false;
  videoPreview: string | ArrayBuffer | null = null;
  videoDirection = 0;
  loadingWishlistItems = false;
  disabledBtn = false;
  listLinkAddWishlist: string[] = [];
  isMobileScreen = false;

  getScreenSize() {
    this.isMobileScreen = window.innerWidth <= 767;
  }

  constructor(
    public ref: DynamicDialogRef,
    private fb: FormBuilder,
    injector: Injector,
    private onboardingService: OnboardingService,
    private appUserService: AppUserService,
    public config: DynamicDialogConfig,
    private http: HttpClient
  ) {
    super(injector);
    this.initializeForms();
    if (this.config.data && this.config.data.step) {
      // Nếu có, sử dụng giá trị đó để xác định bước hiện tại
      this.currentStep = this.config.data.step;
    }

    // Lấy callback function từ data
    if (this.config.data && this.config.data.onStepChange) {
      this.onStepChangeCallback = this.config.data.onStepChange;
    }

    this.loadOnboardingData();

    // Fetch random wishlist items
    this.loadRandomWishlistItems();

    // Lấy danh sách các sản phẩm đã thêm từ session storage
    this.loadAddedWishlistItems();
  }

  private initializeForms() {
    this.profileForm = this.fb.group({
      displayName: [undefined, [Validators.required, ValidationComponent.checkSpace]],
      username: [undefined, [Validators.required, ValidationComponent.formatAcountName]],
      avatar: [null],
      cover: [null],
      video: [null],
    });
    this.addressForm = this.fb.group({
      deliveryName: [undefined, [Validators.required, ValidationComponent.checkSpace]],
      postCode: [undefined, [ValidationComponent.formatPostCode, Validators.required]],
      prefecture: [undefined, [Validators.required]],
      municipality: [undefined, [Validators.required, ValidationComponent.checkSpace]],
      townArea: [
        undefined,
        [
          Validators.required,
          ValidationComponent.checkSpace,
          ValidationComponent.checkRomanNumeral,
        ],
      ],
      buildingName: [undefined, [ValidationComponent.checkRomanNumeral]],
      telephone: [undefined, [Validators.required]],
    });
  }

  // Call API to get onboarding data
  private loadOnboardingData() {
    this.onboardingService.getInforOnboarding().subscribe(
      (data: GetInforOnboardingDto) => {
        this.onboardingData = data;
        this.currentStep = data.onboardingStep ?? OnboardingStep.Step1_Profile;
        if (isNullOrEmpty(data.onboardingStep)) {
          this.updateOnboardingStep(OnboardingStep.Step1_Profile);
        }

        // Only assign data for the current step
        this.updateFormForCurrentStep();
      },
      error => {
        console.error('Error when getting onboarding data:', error);
      }
    );
  }

  // Update form for the current step
  private updateFormForCurrentStep() {
    if (!this.onboardingData) return;

    if (this.onboardingData.creatorProfile) {
      this.profileForm.patchValue({
        displayName: this.onboardingData.creatorProfile.name,
        username: this.onboardingData.creatorProfile.userName,
      });
    }

    this.avatarFileName = this.getFileName(this.onboardingData.creatorInforMedia.profileImage);
    this.coverFileName = this.getFileName(this.onboardingData.creatorInforMedia.coverImage);
    this.videoFileName = this.getFileName(this.onboardingData.creatorInforMedia.shortVideo);

    this.addressForm.patchValue({
      deliveryName: this.onboardingData.creatorInfoAddress.deliveryName,
      postCode: this.onboardingData.creatorInfoAddress.postCode,
      prefecture: this.onboardingData.creatorInfoAddress.prefectures,
      municipality: this.onboardingData.creatorInfoAddress.municipalities,
      townArea: this.onboardingData.creatorInfoAddress.townAreaBlockNumber,
      buildingName: this.onboardingData.creatorInfoAddress.address,
      telephone: this.onboardingData.creatorInfoAddress.phoneNumber,
    });
    if (this.addressForm.controls.postCode.value) {
      this.appUserService
        .getPrefecturesByPostCodeByInput(this.addressForm.controls.postCode.value)
        .pipe(
          catchError(err => {
            return err;
          }),
          finalize(() => {})
        )
        .subscribe((rs: any) => {
          if (rs.length > 0) {
            this.prefectureOptions = rs;
            this.addressForm.controls.prefecture.setValue(
              this.onboardingData.creatorInfoAddress.prefectures
            );
          }
        });
    }
  }

  async nextStep() {
    // Always mark form as touched to show validation errors
    this.markCurrentFormTouched();

    const isCurrentStepValid = this.validateCurrentStep();

    if (isCurrentStepValid) {
      await this.updateCurrentStepData(true, this.currentStep);

      this.currentStep++;

      // Gọi callback để thông báo thay đổi step
      if (this.onStepChangeCallback) {
        this.onStepChangeCallback(this.currentStep);
      }

      if (this.currentStep == OnboardingStep.Step5_Done) {
        this.close();
      }
    }
    // If form is invalid, validation errors will show because we marked it as touched
  }

  private getFileName(path: string) {
    if (isNullOrEmpty(path)) return null;
    return path.split('/').pop();
  }

  private markCurrentFormTouched() {
    const currentComponent = this.steps[this.currentStep].component;

    switch (currentComponent) {
      case 'profile':
        this.markFormGroupTouched(this.profileForm);
        break;
      case 'address':
        this.markFormGroupTouched(this.addressForm);
        break;
      // Removed 'social' case as socialForm doesn't exist
    }
  }

  validateCurrentStep(): boolean {
    const currentComponent = this.steps[this.currentStep].component;

    switch (currentComponent) {
      case 'profile':
        return this.profileForm.valid;
      case 'address':
        return this.addressForm.valid;
      case 'wishlist':
        // Wishlist step is always valid (no required fields)
        return true;
      case 'share':
        // Share step is always valid (no required fields)
        return true;
      default:
        return true;
    }
  }

  async previousStep() {
    // Don't validate when going back, just mark as touched for current form
    this.markCurrentFormTouched();
    const isCurrentStepValid = this.validateCurrentStep();

    if (isCurrentStepValid) {
      if (this.currentStep > 0) {
        // Save current step data before going back (optional validation)
        const isCurrentStepValid = this.validateCurrentStep();
        if (isCurrentStepValid) {
          await this.updateCurrentStepData(false, this.currentStep);
        }

        this.currentStep--;

        // Gọi callback để thông báo thay đổi step
        if (this.onStepChangeCallback) {
          this.onStepChangeCallback(this.currentStep);
        }
      }
    }
  }

  // Mark all fields in the form as touched and dirty to display validation errors
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      control.markAsDirty(); // Add this line to trigger validation display

      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  close() {
    // Xóa session storage khi đóng popup
    this.clearAddedWishlistItems();
    this.ref.close({ onboardingStep: this.currentStep });
  }

  onAvatarChange(event: any) {
    if (event.target.files.length > 0) {
      this.loadingAvatar = true;
      const file = event.target.files[0];
      const arr = file.name.split('.');
      const fileType = arr[arr.length - 1].toString().toLowerCase();
      if (file.size > 5242880 || !this.imageCheck.includes(fileType)) {
        this.showErrorMessage(this.L('::Wishlist:Creator:UpdateImgErrorSize'));
        this.loadingAvatar = false;
        event.target.value = ''; // reset value when file upload error
        return;
      } else {
        const reader = new FileReader();
        reader.addEventListener(
          'load',
          () => {
            // convert image file to base64 string
            let formData = new FormData();
            formData.append('anhDaiDien', file);
            this.http
              .post(this.URLUploadAvatar, formData, { responseType: 'text' })
              .pipe(
                catchError(err => {
                  this.handlerErrorUploadfile(JSON.parse(err.error));
                  return err;
                }),
                finalize(() => {
                  this.loadingAvatar = false;
                })
              )
              .subscribe(() => {
                this.avatarFileName = file.name;
                this.mediaForm.patchValue({ avatar: file });
                this.avatarPreview = reader.result as string;
              });
          },
          false
        );
        reader.readAsDataURL(file);
      }
    }
  }

  onCoverChange(event: any) {
    if (event.target.files.length > 0) {
      this.loadingCover = true;
      const file = event.target.files[0];
      const arr = file.name.split('.');
      const fileType = arr[arr.length - 1].toString().toLowerCase();
      if (file.size > 5242880 || !this.imageCheck.includes(fileType)) {
        this.showErrorMessage(this.L('::Wishlist:Creator:UpdateImgErrorSize'));
        this.loadingCover = false;
        event.target.value = ''; // reset value when file upload error
        return;
      } else {
        const reader = new FileReader();
        reader.addEventListener(
          'load',
          () => {
            // convert image file to base64 string
            let formData = new FormData();
            formData.append('anhCover', file);
            this.http
              .post(this.URLUploadCover, formData, { responseType: 'text' })
              .pipe(
                catchError(err => {
                  this.handlerErrorUploadfile(JSON.parse(err.error));
                  return err;
                }),
                finalize(() => {
                  this.loadingCover = false;
                })
              )
              .subscribe(() => {
                this.coverFileName = file.name;
                this.mediaForm.patchValue({ cover: file });
                this.coverPreview = reader.result as string;
              });
          },
          false
        );
        reader.readAsDataURL(file);
      }
    }
  }

  onVideoChange(event: any) {
    if (event.target.files.length > 0) {
      this.loadingVideo = true;
      const file = event.target.files[0];
      const arr = file.name.split('.');
      const fileType = arr[arr.length - 1].toString().toLowerCase();
      if (file.size > 104857600 || !this.videoCheck.includes(fileType)) {
        this.showErrorMessage(this.L('::Wishlist:Creator:UpdateVideoErrorSize'));
        this.loadingVideo = false;
        event.target.value = ''; // reset value when file upload error
        return;
      } else {
        const reader = new FileReader();
        reader.onload = () => {
          const video = document.createElement('video');
          video.src = URL.createObjectURL(file);
          video.onloadedmetadata = () => {
            const aspectRatio = video.videoWidth / video.videoHeight;
            if (aspectRatio > 1) {
              this.videoDirection = 1;
            } else {
              this.videoDirection = 0;
            }
            let formData = new FormData();
            formData.append('shortVideo', file);
            this.http
              .post(this.URLUploadVideo + this.videoDirection, formData, { responseType: 'text' })
              .pipe(
                catchError(err => {
                  this.handlerErrorUploadfile(JSON.parse(err.error));
                  return err;
                }),
                finalize(() => {
                  this.loadingVideo = false;
                })
              )
              .subscribe(() => {
                this.videoFileName = file.name;
                this.mediaForm.patchValue({ video: file });
                this.videoPreview = URL.createObjectURL(file);
              });
          };
        };
        reader.readAsDataURL(file);
      }
    }
  }

  // Add product to selected wishlist
  toggleWishlistItem(item: any) {
    const index = this.selectedWishlistItems.findIndex(i => i.id === item.id);
    if (index === -1) {
      // If product is not selected, add to list
      this.selectedWishlistItems.push(item);
    } else {
      // If the product is already selected, remove it from the list.
      this.selectedWishlistItems.splice(index, 1);
    }
  }

  // Check if the product has been selected
  isItemSelected(itemId: string | number): boolean {
    return this.selectedWishlistItems.some(item => item.id === itemId);
  }

  // Get image URL
  getImageUrl(url: string): string {
    return url || 'assets/images/placeholder.jpg';
  }

  // Get domain name from URL
  getLinkDomain(url: string): string {
    try {
      const domain = new URL(url);
      return domain.hostname.replace('www.', '');
    } catch (e) {
      return url;
    }
  }

  checkAccountName(event) {
    if (event.target.value.trim() !== '') {
      this.appUserService.checkAccountNameIsExistByInput(event.target.value.trim()).subscribe(s => {
        if (!s) {
          if (this.profileForm.get('username').errors !== null) {
            delete this.profileForm.get('username').errors.acountNameIsExist;
            this.profileForm.get('username').updateValueAndValidity();
          }
        } else {
          this.profileForm.get('username').setErrors({ acountNameIsExist: true });
        }
      });
    }
  }

  // Update the data of the current step into the database
  private async updateCurrentStepData(isNext: boolean, currentStep: OnboardingStep) {
    try {
      switch (currentStep) {
        case OnboardingStep.Step1_Profile:
          await this.updateOnboardingStepProfile();
          break;
        case OnboardingStep.Step2_Address:
          await this.updateOnboardingStepAddress();
          break;
        case OnboardingStep.Step3_Wishlist:
          // Lọc ra các sản phẩm mới chưa được thêm (theo ID)
          const newItems = this.selectedWishlistItems.filter(
            item => item.id && !this.addedWishlistItems.includes(item.id.toString())
          );
          if (newItems.length > 0) {
            this.addWishlistItems(newItems.map(item => item.id));
            // Cập nhật danh sách sản phẩm đã thêm (lưu ID)
            newItems.forEach(item => {
              if (item.id) this.addedWishlistItems.push(item.id.toString());
            });
            // Lưu danh sách các sản phẩm đã thêm vào session storage
            this.saveAddedWishlistItems();
          }
          break;
        default:
          break;
      }

      // Thêm delay nhỏ để tránh conflict
      await new Promise(resolve => setTimeout(resolve, 300));

      const updateOnboardingStep: OnboardingStep = isNext ? currentStep + 1 : currentStep - 1;
      await this.updateOnboardingStep(updateOnboardingStep);
    } catch (error) {
      console.error('Error updating step data:', error);
      // Reload fresh data from server
      this.loadOnboardingData();
      throw error;
    }
  }

  // Handler for upload file errors
  public handlerErrorUploadfile(err: any) {
    if (err && err.error && err.error.code) {
      this.showErrorMessage(this.L('::' + err.error.code));
    } else if (err && err.error && err.error.message) {
      this.showErrorMessage(err.error.message);
    } else {
      this.showErrorMessage(this.L('::Wishlist:Creator:UpdateImgErrorSize'));
    }
  }

  onChangePostCode(event: any) {
    // convert fullsize to halfsize
    const valueConvert = this.convertToHalfSize(event.target.value);
    if (valueConvert && valueConvert.length >= 7) {
      // check valueConvert of form xxx-
      const isPostCode = /^\d{3}-/.test(valueConvert);
      if (isPostCode) {
        // Save first 8 characters
        const valueSlice = valueConvert.slice(0, 8);
        this.addressForm.get('postCode').patchValue(valueSlice);
        this.handleChangePostcode(valueSlice);
      } else {
        // Save first 7 characters
        const valueSlice = valueConvert.slice(0, 7);
        // Change valueSlice to form xxx-xxxx
        const value = valueSlice.replace(/(\d{3})(\d{0,4})/, '$1-$2');
        this.addressForm.get('postCode').patchValue(value);
        this.handleChangePostcode(value);
      }
    } else {
      this.addressForm.get('postCode').patchValue(valueConvert);
      this.handleChangePostcode(valueConvert);
    }
  }

  handleChangePostcode(value: any) {
    if (value.trim().length == 0) {
      this.addressForm.controls.municipality.reset();
      this.addressForm.controls.townArea.reset();
      this.prefectureOptions = [];
    } else {
      this.appUserService.getPrefecturesByPostCodeByInput(value).subscribe(rs => {
        if (rs.length > 0) {
          this.prefectureOptions = rs;
          this.addressForm.controls.prefecture.setValue(rs[0].id);
          this.onPrefecturesChange(rs[0].id);
        } else {
          this.checkPostCode(value);
          this.addressForm.controls.municipality.reset();
          this.addressForm.controls.townArea.reset();
          this.prefectureOptions = [];
        }
      });
    }
  }

  onPrefecturesChange(value: any) {
    this.appUserService.getInforByPrefecturesByInput(value).subscribe(rs => {
      this.addressForm.controls.municipality.setValue(rs.municipalities);
      this.addressForm.controls.townArea.setValue(rs.townAreaBlockNumber);
    });
  }

  checkPostCode(value) {
    if (value.replace(/_/g, '').trim().length === 8) {
      this.appUserService.getPrefecturesByPostCodeByInput(value.trim()).subscribe(s => {
        if (s.length > 0) {
          if (this.addressForm.get('postCode').errors !== null) {
            delete this.addressForm.get('postCode').errors.postCodeNotExist;
            this.addressForm.get('postCode').updateValueAndValidity();
          }
        } else {
          this.addressForm.get('postCode').setErrors({ postCodeNotExist: true });
        }
      });
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    const char = event.key;
    const validChars = /^-?(0|[1-9]\d*)?$/;
    if (!validChars.test(char) && event.key !== 'Backspace') {
      event.preventDefault();
    }
  }

  checkPhone(event: any) {
    // Convert fullsize sang halfsize
    const value = this.convertToHalfSize(event.target.value);
    this.addressForm.controls.telephone.setValue(value);

    if (event.target.value.trim() !== '') {
      if (event.target.value.trim().length < 11) {
        this.addressForm.get('telephone').setErrors({ lengthPhone: true });
      }
    } else if (this.addressForm.get('telephone').errors !== null) {
      delete this.addressForm.get('telephone').errors.lengthPhone;
      this.addressForm.get('telephone').updateValueAndValidity();
    }
  }

  // Fetch random wishlist items from API
  private loadRandomWishlistItems() {
    this.loadingWishlistItems = true;
    this.onboardingService.getRandomWishListItemsByCount(8).subscribe(
      items => {
        this.sampleWishlistItems = items;
        this.loadingWishlistItems = false;
      },
      error => {
        console.error('Error loading wishlist items:', error);
        this.loadingWishlistItems = false;
      }
    );
  }

  // Convert fullsize characters to halfsize
  public convertToHalfSize(value: string): string {
    if (!value) return '';
    return value.replace(/[！-～]/g, function (s) {
      return String.fromCharCode(s.charCodeAt(0) - 0xfee0);
    });
  }

  shareOnTwitter() {
    // Lấy nội dung từ localization và chuyển đổi HTML thành text thuần túy
    let tweetContent = this.L('::Onboarding:Share:Content');

    // Chuyển đổi HTML tags thành line breaks cho Twitter
    tweetContent =
      tweetContent
        .replace(/<br\s*\/?>/gi, '\n') // Thay thế <br> và <br/> bằng line break
        .replace(/<\/p>/gi, '\n\n') // Thay thế </p> bằng double line break
        .replace(/<[^>]*>/g, '') // Loại bỏ tất cả HTML tags còn lại
        .replace(/&nbsp;/gi, ' ') // Thay thế &nbsp; bằng space
        .replace(/&amp;/gi, '&') // Thay thế &amp; bằng &
        .replace(/&lt;/gi, '<') // Thay thế &lt; bằng <
        .replace(/&gt;/gi, '>') // Thay thế &gt; bằng >
        .trim() + '\n'; // Loại bỏ whitespace đầu cuối và thêm line break cuối

    const tweetText = encodeURIComponent(tweetContent);

    // URL của trang wishlist (có thể thay đổi tùy theo yêu cầu thực tế)
    const wishlistUrl = encodeURIComponent(
      `${this.baseUrl}/main/wishlist/fan-view/${this.onboardingData?.id}`
    );

    // Mở cửa sổ chia sẻ Twitter
    const twitterUrl = `https://twitter.com/intent/tweet?text=${tweetText}&url=${wishlistUrl}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
  }

  private async updateOnboardingStep(input: OnboardingStep): Promise<any> {
    return this.onboardingService.updateOnboardingStepByOnboardingStep(input).toPromise();
  }

  private async updateOnboardingStepProfile(): Promise<any> {
    const input: CreatorProfileDto = {
      name: this.profileForm.value.displayName,
      userName: this.profileForm.value.username,
    };
    return this.onboardingService.updateStepProfile(input).toPromise();
  }

  private async updateOnboardingStepAddress(): Promise<any> {
    const input: CreatorInfoAddressDto = {
      deliveryName: this.addressForm.value.deliveryName,
      postCode: this.addressForm.value.postCode,
      prefectures: this.addressForm.value.prefecture,
      municipalities: this.addressForm.value.municipality,
      townAreaBlockNumber: this.addressForm.value.townArea,
      address: this.addressForm.value.buildingName,
      phoneNumber: this.addressForm.value.telephone,
    };
    return this.onboardingService.updateStepAddress(input).toPromise();
  }

  private addWishlistItems(links: number[]): void {
    // Gọi API và để nó chạy ở background
    this.onboardingService.addGiftForLinkByInput(links).subscribe(
      res => {
        this.handleNotificationWhenAddLink(links.length, res);
        // this.showSuccessMessage(this.L('::Onboarding:Wishlist:Success'));
      },
      error => {
        console.error('Error adding wishlist items:', error);
      }
    );
  }

  private handleNotificationWhenAddLink(numberOfLink: number, linkAddSuccess: number) {
    if (numberOfLink == linkAddSuccess) {
      this.showSuccessMessage(this.L('::Onboarding:Wishlist:Success'));
      return;
    } else if (numberOfLink == 1 && linkAddSuccess == 0) {
      this.showErrorMessage(this.L('::CreatorDonate:CannotGetQuantity'));
      return;
    } else if (numberOfLink > 0 && linkAddSuccess) {
      this.showWarningMessage(
        this.localizationService.instant(
          '::Onboarding:Wishlist:Notification:OutOfStock',
          numberOfLink.toString(),
          (numberOfLink - linkAddSuccess).toString()
        )
      );
    }
  }

  // Lấy danh sách các sản phẩm đã thêm từ session storage (theo ID)
  private loadAddedWishlistItems() {
    const storedItems = sessionStorage.getItem('onboarding_added_wishlist_items');
    if (storedItems) {
      this.addedWishlistItems = JSON.parse(storedItems);
    }
  }

  // Lưu danh sách các sản phẩm đã thêm vào session storage (theo ID)
  private saveAddedWishlistItems() {
    sessionStorage.setItem(
      'onboarding_added_wishlist_items',
      JSON.stringify(this.addedWishlistItems)
    );
  }

  // Xóa danh sách các sản phẩm đã thêm khỏi session storage
  private clearAddedWishlistItems() {
    sessionStorage.removeItem('onboarding_added_wishlist_items');
    this.addedWishlistItems = [];
  }

  // Character count tracking methods
  public readonly MAX_LENGTHS = {
    displayName: 50,
    username: 50,
  } as const;

  getCharacterCount(controlName: string, formName: string = 'profileForm'): number {
    const form = formName === 'addressForm' ? this.addressForm : this.profileForm;
    const control = form.get(controlName);
    return control?.value?.length || 0;
  }

  getMaxLength(controlName: string): number {
    return this.MAX_LENGTHS[controlName as keyof typeof this.MAX_LENGTHS] || 0;
  }
}
